import { useState, useEffect, useMemo } from 'react';
import { Save, User, MapPin, IdCard, Briefcase, Landmark, History, Users as UsersIcon, ShieldCheck, IndianRupee, Plus, Trash2, Pencil, X, Paperclip, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanySettings } from '@/api/queries/settings.queries';
import { useDepartments, useDesignations, useEmployees, useCreateDepartment, useCreateDesignation } from '@/api/queries/employee.queries';
import { PhotoUpload } from '@/components/shared/PhotoUpload';
import { Button } from '@/components/ui/button';
import { EmployeeFieldsModal } from './EmployeeFieldsModal';
import { DocumentsSection } from './DocumentsSection';
import { CustomFieldBuilderModal } from './CustomFieldBuilderModal';
import { FIELD_CATEGORIES, getFieldsByCategory } from '@/lib/employeeFields';

const CATEGORY_ICONS = {
  personal: User, contact: MapPin, government_ids: IdCard, employment: Briefcase,
  bank_salary: Landmark, previous_employment: History, nominee_family: UsersIcon, witness: ShieldCheck,
};

const SUBFIELD_META = {
  name: { label: 'Name', type: 'text' },
  relation: { label: 'Relation', type: 'text' },
  dob: { label: 'Date of Birth', type: 'date' },
  aadhaar_number: { label: 'Aadhaar Number', type: 'text' },
  address: { label: 'Address', type: 'textarea' },
};

function Field({ label, required, error, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[13px] font-semibold text-[var(--text-primary)]">
        {label}{required && <span className="text-[var(--danger)] ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-[12px] text-[var(--danger)]">{error}</p>}
    </div>
  );
}

const inputCls = "w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all";
const textareaCls = "w-full min-h-[72px] px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all";

function CategoryHeader({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 pb-1 border-b border-[var(--border)]">
      <Icon size={15} className="text-[var(--accent)]"/>
      <h3 className="text-[13px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">{label}</h3>
    </div>
  );
}

function ObjectFieldGroup({ field, value, onChange }) {
  const v = value || {};
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 rounded-[10px] bg-[var(--bg-muted)]">
      {field.fields.map(sub => {
        const meta = SUBFIELD_META[sub] || { label: sub, type: 'text' };
        return (
          <Field key={sub} label={meta.label}>
            {meta.type === 'date'
              ? <input type="date" value={v[sub] || ''} onChange={e => onChange({ ...v, [sub]: e.target.value })} className={inputCls}/>
              : meta.type === 'textarea'
              ? <textarea value={v[sub] || ''} onChange={e => onChange({ ...v, [sub]: e.target.value })} className={textareaCls}/>
              : <input value={v[sub] || ''} onChange={e => onChange({ ...v, [sub]: e.target.value })} className={inputCls}/>
            }
          </Field>
        );
      })}
    </div>
  );
}

function RepeatableFieldGroup({ field, value, onChange }) {
  const rows = value || [];
  const updateRow = (i, k, v) => onChange(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => onChange([...rows, {}]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {rows.map((row, i) => (
        <div key={i} className="flex items-start gap-3 p-3 rounded-[10px] bg-[var(--bg-muted)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
            {field.fields.map(sub => {
              const meta = SUBFIELD_META[sub] || { label: sub, type: 'text' };
              return (
                <Field key={sub} label={meta.label}>
                  {meta.type === 'date'
                    ? <input type="date" value={row[sub] || ''} onChange={e => updateRow(i, sub, e.target.value)} className={inputCls}/>
                    : <input value={row[sub] || ''} onChange={e => updateRow(i, sub, e.target.value)} className={inputCls}/>
                  }
                </Field>
              );
            })}
          </div>
          <button type="button" onClick={() => removeRow(i)}
            className="p-2 mt-6 rounded-[6px] hover:bg-[var(--danger)]/10 text-[var(--text-tertiary)] hover:text-[var(--danger)] transition-colors shrink-0">
            <Trash2 size={14}/>
          </button>
        </div>
      ))}
      <button type="button" onClick={addRow}
        className="flex items-center gap-1.5 text-[12px] font-medium text-[var(--accent)] hover:underline">
        <Plus size={13}/> Add {field.label.replace(/s$/, '')}
      </button>
    </div>
  );
}

/** Department <select> + an inline "add new department" affordance, so a
 *  missing department doesn't require a trip to Settings → Departments. */
function QuickAddDepartmentSelect({ value, onChange, departments, onCreateDepartment }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const handleAdd = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const dept = await onCreateDepartment(name.trim());
      onChange(dept.id);
      setName(''); setAdding(false);
      toast.success(`"${dept.name}" added`);
    } catch {
      toast.error('Failed to add department');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <select value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls}>
          <option value="">Select department</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button type="button" onClick={() => setAdding(a => !a)} title="Add new department"
          className="shrink-0 w-10 h-10 flex items-center justify-center rounded-[10px] border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)] transition-colors">
          <Plus size={15}/>
        </button>
      </div>
      {adding && (
        <div className="flex gap-2">
          <input autoFocus value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="New department name" className={inputCls}/>
          <Button type="button" size="sm" onClick={handleAdd} disabled={busy}>{busy ? '…' : 'Add'}</Button>
          <button type="button" onClick={() => { setAdding(false); setName(''); }}
            className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><X size={15}/></button>
        </div>
      )}
    </div>
  );
}

/** Designation free-text input (with autocomplete) + an inline "add new
 *  designation" affordance that also saves it to the master list. */
function QuickAddDesignationInput({ value, onChange, designations, onCreateDesignation }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const handleAdd = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const desig = await onCreateDesignation(name.trim());
      onChange(desig.name);
      setName(''); setAdding(false);
      toast.success(`"${desig.name}" added`);
    } catch {
      toast.error('Failed to add designation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input list="designation-options" value={value || ''} onChange={e => onChange(e.target.value)}
          placeholder="e.g. Software Engineer" className={inputCls}/>
        <datalist id="designation-options">{designations.map(d => <option key={d.id} value={d.name}/>)}</datalist>
        <button type="button" onClick={() => setAdding(a => !a)} title="Add new designation"
          className="shrink-0 w-10 h-10 flex items-center justify-center rounded-[10px] border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)] transition-colors">
          <Plus size={15}/>
        </button>
      </div>
      {adding && (
        <div className="flex gap-2">
          <input autoFocus value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="New designation name" className={inputCls}/>
          <Button type="button" size="sm" onClick={handleAdd} disabled={busy}>{busy ? '…' : 'Add'}</Button>
          <button type="button" onClick={() => { setAdding(false); setName(''); }}
            className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><X size={15}/></button>
        </div>
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange, departments, designations, employees, onCreateDepartment, onCreateDesignation }) {
  switch (field.type) {
    case 'boolean':
      return (
        <label className="flex items-center gap-2.5 h-10 cursor-pointer select-none">
          <input type="checkbox" checked={value !== false} onChange={e => onChange(e.target.checked)}
            className="w-4 h-4 rounded accent-[var(--accent)]"/>
          <span className="text-[13px] text-[var(--text-primary)]">{field.label}</span>
        </label>
      );
    case 'textarea':
      return <textarea value={value || ''} onChange={e => onChange(e.target.value)} className={textareaCls}/>;
    case 'enum':
      return (
        <select value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls}>
          <option value="">Select…</option>
          {field.options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
        </select>
      );
    case 'date':
      return <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls}/>;
    case 'number':
      return <input type="number" min="0" value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls}/>;
    case 'email':
      return <input type="email" value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls}/>;
    case 'phone':
      return <input value={value || ''} onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))} className={inputCls}/>;
    case 'select_department':
      return <QuickAddDepartmentSelect value={value} onChange={onChange} departments={departments} onCreateDepartment={onCreateDepartment}/>;
    case 'select_designation':
      return <QuickAddDesignationInput value={value} onChange={onChange} designations={designations} onCreateDesignation={onCreateDesignation}/>;
    case 'select_employee':
      return (
        <select value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls}>
          <option value="">None</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</option>)}
        </select>
      );
    case 'object':
      return <ObjectFieldGroup field={field} value={value} onChange={onChange}/>;
    case 'repeatable':
      return <RepeatableFieldGroup field={field} value={value} onChange={onChange}/>;
    default:
      return <input value={value || ''} onChange={e => onChange(e.target.value)} className={inputCls}/>;
  }
}

function CategoryCard({ category, fields, getValue, setValue, errors, departments, designations, employees, onCreateDepartment, onCreateDesignation }) {
  const Icon = CATEGORY_ICONS[category.key] || User;
  const visible = fields.filter(f => {
    if (f.key === 'employee_code' || f.type === 'photo') return false;
    if (f.dependsOn) {
      const depVal = getValue({ key: f.dependsOn.field, target: 'extended' });
      return f.dependsOn.values.includes(depVal);
    }
    return true;
  });
  if (!visible.length) return null;
  return (
    <div className="card space-y-4 p-5">
      <CategoryHeader icon={Icon} label={category.label}/>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {visible.map(f => {
          const wide = f.type === 'object' || f.type === 'repeatable' || f.type === 'textarea' || f.type === 'select_department' || f.type === 'select_designation';
          if (f.type === 'boolean') {
            return (
              <div key={f.key} className="sm:col-span-2">
                <FieldInput field={f} value={getValue(f)} onChange={v => setValue(f, v)}/>
              </div>
            );
          }
          return (
            <div key={f.key} className={wide ? 'sm:col-span-2' : ''}>
              <Field label={f.label} required={f.required} error={errors[f.key]}>
                <FieldInput field={f} value={getValue(f)} onChange={v => setValue(f, v)}
                  departments={departments} designations={designations} employees={employees}
                  onCreateDepartment={onCreateDepartment} onCreateDesignation={onCreateDesignation}/>
              </Field>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Shared Add/Edit Employee form. Renders one card per registry category —
 * Personal Information and Employment/Bank cards always show (they contain
 * locked fields); every other category only renders if the company has at
 * least one field enabled for it via Settings → Customize Employee Fields.
 */
export function EmployeeForm({ initialData, onSubmit, submitting, serverError }) {
  const { data: settingsResp } = useCompanySettings();
  const enabledList = settingsResp?.data?.settings?.employee_form_fields || [];

  const { data: deptsResp } = useDepartments();
  const departments = deptsResp?.data || [];
  const { data: desigResp } = useDesignations();
  const designations = desigResp?.data || [];
  const { data: empsResp } = useEmployees({ limit: 200 });
  const employees = (empsResp?.data || []).filter(e => e.id !== initialData?.id);

  const createDepartment = useCreateDepartment();
  const createDesignation = useCreateDesignation();
  const handleQuickAddDepartment = async (name) => (await createDepartment.mutateAsync({ name })).data;
  const handleQuickAddDesignation = async (name) => (await createDesignation.mutateAsync({ name })).data;

  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const [documents, setDocuments] = useState([]);

  const [core, setCore] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    department_id: '', designation: '', employment_type: 'FULL_TIME', joining_date: '',
    photo_url: null, bank_name: '', account_holder_name: '', account_number: '', ifsc_code: '', reporting_manager_id: '',
    ctc_annual: '', gross_monthly: '', basic: '', daily_rate: '',
  });
  const [extended, setExtended] = useState({});
  const [errors, setErrors] = useState({});

  // These must come AFTER extended is declared above
  const customFields = settingsResp?.data?.settings?.custom_employee_fields || [];
  const customValues = extended.custom || {};
  const setCustomValue = (key, v) => setExtField('custom', { ...customValues, [key]: v });

  const duplicateNameWarning = useMemo(() => {
    if (!core.first_name?.trim() || !core.last_name?.trim()) return null;
    const fullName = `${core.first_name.trim()} ${core.last_name.trim()}`.toLowerCase();
    const match = employees.find(e => `${e.first_name} ${e.last_name}`.toLowerCase() === fullName);
    return match ? `An employee named "${match.first_name} ${match.last_name}" (${match.employee_code}) already exists.` : null;
  }, [core.first_name, core.last_name, employees]);

  const duplicatePhoneWarning = useMemo(() => {
    if (!core.phone?.trim() || core.phone.trim().length < 10) return null;
    const match = employees.find(e => e.phone === core.phone.trim());
    return match ? `Phone number already used by ${match.first_name} ${match.last_name} (${match.employee_code}).` : null;
  }, [core.phone, employees]);

  useEffect(() => {
    if (!initialData) return;
    setCore({
      first_name: initialData.first_name || '',
      last_name: initialData.last_name || '',
      phone: initialData.phone || '',
      email: initialData.email || '',
      department_id: initialData.department_id || initialData.department?.id || '',
      designation: initialData.designation || '',
      employment_type: initialData.employment_type || 'FULL_TIME',
      joining_date: initialData.joining_date?.slice(0, 10) || '',
      photo_url: initialData.photo_url || null,
      bank_name: initialData.bank_name || '',
      account_holder_name: initialData.account_holder_name || '',
      account_number: initialData.account_number || '',
      ifsc_code: initialData.ifsc_code || '',
      reporting_manager_id: initialData.reporting_manager_id || initialData.reporting_manager?.id || '',
      ctc_annual: initialData.salary?.ctc_annual || '',
      gross_monthly: initialData.salary?.gross_monthly || '',
      basic: initialData.salary?.basic || '',
      daily_rate: initialData.salary?.daily_rate || '',
    });
    setExtended(initialData.extended_details || {});
    setDocuments(initialData.extended_details?.documents || []);
  }, [initialData]);

  const setCoreField = (k, v) => { setCore(c => ({ ...c, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: '' })); };
  const setExtField = (k, v) => { setExtended(x => ({ ...x, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: '' })); };

  const getValue = (field) => field.target === 'column' ? core[field.key] : extended[field.key];
  const setValue = (field, v) => field.target === 'column' ? setCoreField(field.key, v) : setExtField(field.key, v);

  const validate = () => {
    const e = {};
    if (!core.first_name.trim()) e.first_name = 'Required';
    if (!core.last_name.trim()) e.last_name = 'Required';
    if (!core.joining_date) e.joining_date = 'Required';
    if (!core.bank_name.trim()) e.bank_name = 'Required';
    if (!core.account_holder_name.trim()) e.account_holder_name = 'Required';
    if (!core.account_number.trim()) e.account_number = 'Required';
    if (!core.ifsc_code.trim()) e.ifsc_code = 'Required';
    else if (!/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(core.ifsc_code.trim())) e.ifsc_code = 'Invalid IFSC format (e.g. HDFC0001234)';
    if (!core.phone.trim()) e.phone = 'Required';
    else if (!/^\d{10}$/.test(core.phone)) e.phone = 'Must be 10 digits';
    if (core.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(core.email)) e.email = 'Invalid email';
    if (!extended.dob) e.dob = 'Required';
    if (!extended.gender) e.gender = 'Required';
    if (!extended.aadhaar_number?.trim()) e.aadhaar_number = 'Required';
    else if (!/^\d{12}$/.test(extended.aadhaar_number.trim())) e.aadhaar_number = 'Must be 12 digits';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const isDailyWage = core.employment_type === 'DAILY_WAGE';
    const salary = isDailyWage
      ? { daily_rate: Number(core.daily_rate) || 0 }
      : { ctc_annual: Number(core.ctc_annual) || 0, gross_monthly: Number(core.gross_monthly) || 0, basic: Number(core.basic) || 0 };

    onSubmit({
      first_name: core.first_name.trim(),
      last_name: core.last_name.trim(),
      phone: core.phone || null,
      email: core.email || null,
      department_id: core.department_id || null,
      designation: core.designation || null,
      employment_type: core.employment_type,
      joining_date: core.joining_date,
      photo_url: core.photo_url || null,
      bank_name: core.bank_name.trim(),
      account_holder_name: core.account_holder_name.trim(),
      account_number: core.account_number.trim(),
      ifsc_code: core.ifsc_code.trim().toUpperCase(),
      reporting_manager_id: core.reporting_manager_id || null,
      salary,
      extended_details: { ...extended, documents },
    });
  };

  const isDailyWage = core.employment_type === 'DAILY_WAGE';
  const personalFields = getFieldsByCategory('personal', enabledList);
  const showPhoto = personalFields.some(f => f.key === 'photo_url');

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-5">

      {/* Customize Fields — edit which fields appear without leaving this page */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-[10px] bg-[var(--bg-muted)] border border-[var(--border)]">
        <p className="text-[12px] text-[var(--text-secondary)]">Fields shown below reflect what's enabled for your company</p>
        <button type="button" onClick={() => setShowFieldsModal(true)}
          className="flex items-center gap-1.5 text-[12px] font-semibold text-[var(--accent)] hover:underline shrink-0">
          <Pencil size={12}/> Customize Fields
        </button>
      </div>

      {/* Personal — always shown (locked name fields live here) */}
      <div className="card space-y-4 p-5">
        <CategoryHeader icon={User} label="Personal Information"/>
        {showPhoto && (
          <PhotoUpload name={`${core.first_name} ${core.last_name}`} value={core.photo_url} onChange={v => setCoreField('photo_url', v)}/>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First Name" required error={errors.first_name}>
            <input value={core.first_name} onChange={e => setCoreField('first_name', e.target.value)} placeholder="Priya" className={inputCls}/>
          </Field>
          <Field label="Last Name" required error={errors.last_name}>
            <input value={core.last_name} onChange={e => setCoreField('last_name', e.target.value)} placeholder="Mehta" className={inputCls}/>
          </Field>
          <Field label="Employee Code">
            <input readOnly value={initialData?.employee_code || 'Auto-generated on save'} className={`${inputCls} opacity-60 cursor-not-allowed bg-[var(--bg-muted)]`}/>
          </Field>
          {personalFields.filter(f => !['photo_url', 'first_name', 'last_name'].includes(f.key)).map(f => (
            <Field key={f.key} label={f.label} required={f.required} error={errors[f.key]}>
              <FieldInput field={f} value={getValue(f)} onChange={v => setValue(f, v)} departments={departments} designations={designations} employees={employees}/>
            </Field>
          ))}
        </div>
      </div>

      {/* Remaining categories — conditional, except employment/bank_salary
          which always render because they hold locked fields. */}
      {FIELD_CATEGORIES.filter(c => c.key !== 'personal').map(cat => (
        <CategoryCard key={cat.key} category={cat} fields={getFieldsByCategory(cat.key, enabledList)}
          getValue={getValue} setValue={setValue} errors={errors}
          departments={departments} designations={designations} employees={employees}
          onCreateDepartment={handleQuickAddDepartment} onCreateDesignation={handleQuickAddDesignation}/>
      ))}

      {/* Compensation — not part of the field registry (existing payroll concept) */}
      <div className="card space-y-4 p-5">
        <CategoryHeader icon={IndianRupee} label={`Compensation ${isDailyWage ? '(Daily Wage)' : '(Annual CTC)'}`}/>
        {isDailyWage ? (
          <Field label="Daily Rate (₹)">
            <input type="number" min="0" value={core.daily_rate} onChange={e => setCoreField('daily_rate', e.target.value)} placeholder="500" className={inputCls}/>
          </Field>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Annual CTC (₹)">
              <input type="number" min="0" value={core.ctc_annual} onChange={e => setCoreField('ctc_annual', e.target.value)} placeholder="1200000" className={inputCls}/>
            </Field>
            <Field label="Monthly Gross (₹)">
              <input type="number" min="0" value={core.gross_monthly} onChange={e => setCoreField('gross_monthly', e.target.value)} placeholder="85000" className={inputCls}/>
            </Field>
            <Field label="Basic Salary (₹)">
              <input type="number" min="0" value={core.basic} onChange={e => setCoreField('basic', e.target.value)} placeholder="40000" className={inputCls}/>
            </Field>
          </div>
        )}
        {/* Cash split — shown when payment_mode is CASH or MIXED */}
        {['CASH', 'MIXED'].includes(extended.payment_mode) && !isDailyWage && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-[10px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[13px]">
            <IndianRupee size={15} className="text-amber-600 shrink-0"/>
            <div className="flex-1 space-y-0.5">
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Net Monthly</span>
                <span className="font-semibold text-[var(--text-primary)]">₹{Number(core.gross_monthly || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-amber-700 dark:text-amber-400">
                <span>Cash Portion</span>
                <span className="font-semibold">− ₹{Number(extended.cash_amount || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between border-t border-amber-200 dark:border-amber-700 pt-0.5 font-bold text-[var(--text-primary)]">
                <span>Bank Transfer</span>
                <span>₹{Math.max(0, Number(core.gross_monthly || 0) - Number(extended.cash_amount || 0)).toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>
        )}
        <p className="text-[11px] text-[var(--text-tertiary)]">
          {isDailyWage ? 'Payroll = present days × daily rate. No PF/ESIC applicable.' : 'PF = 12% of basic · ESIC applies if gross < ₹21,000/month · PT as per state slab'}
        </p>
      </div>

      {/* Custom Fields — user-defined, company-specific */}
      <div className="card space-y-4 p-5">
        <div className="flex items-center justify-between pb-1 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <Zap size={15} className="text-[var(--accent)]"/>
            <h3 className="text-[13px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">Custom Fields</h3>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[var(--accent)]/10 text-[var(--accent)]">Your Fields</span>
          </div>
          <button type="button" onClick={() => setShowCustomBuilder(true)}
            className="flex items-center gap-1 text-[12px] font-semibold text-[var(--accent)] hover:underline">
            <Plus size={11}/> {customFields.length ? 'Manage' : 'Add Field'}
          </button>
        </div>
        {customFields.length === 0 ? (
          <p className="text-[13px] text-[var(--text-tertiary)]">
            No custom fields yet.{' '}
            <button type="button" onClick={() => setShowCustomBuilder(true)} className="text-[var(--accent)] hover:underline font-semibold">Add your first one →</button>
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {customFields.map(f => (
              <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                <Field label={f.label} required={f.required}>
                  {f.type === 'checkbox' ? (
                    <label className="flex items-center gap-2.5 h-10 cursor-pointer">
                      <input type="checkbox" checked={!!customValues[f.key]}
                        onChange={e => setCustomValue(f.key, e.target.checked)}
                        className="w-4 h-4 rounded accent-[var(--accent)]"/>
                      <span className="text-[13px] text-[var(--text-primary)]">{f.label}</span>
                    </label>
                  ) : f.type === 'dropdown' ? (
                    <select value={customValues[f.key] || ''} onChange={e => setCustomValue(f.key, e.target.value)} className={inputCls}>
                      <option value="">Select…</option>
                      {(f.options || []).map(o => (
                        <option key={o.label} value={o.label} style={{ color: o.color }}>{o.label}</option>
                      ))}
                    </select>
                  ) : f.type === 'textarea' ? (
                    <textarea value={customValues[f.key] || ''} onChange={e => setCustomValue(f.key, e.target.value)}
                      placeholder={f.placeholder} className={textareaCls}/>
                  ) : f.type === 'date' ? (
                    <input type="date" value={customValues[f.key] || ''} onChange={e => setCustomValue(f.key, e.target.value)} className={inputCls}/>
                  ) : f.type === 'number' ? (
                    <input type="number" value={customValues[f.key] || ''} onChange={e => setCustomValue(f.key, e.target.value)}
                      placeholder={f.placeholder} className={inputCls}/>
                  ) : f.type === 'photo' ? (
                    <PhotoUpload name={f.label} value={customValues[f.key] || null} onChange={v => setCustomValue(f.key, v)}/>
                  ) : (
                    <input value={customValues[f.key] || ''} onChange={e => setCustomValue(f.key, e.target.value)}
                      placeholder={f.placeholder} className={inputCls}/>
                  )}
                </Field>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents & Files */}
      <div className="card space-y-4 p-5">
        <div className="flex items-center gap-2 pb-1 border-b border-[var(--border)]">
          <Paperclip size={15} className="text-[var(--accent)]"/>
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">Documents & Files</h3>
        </div>
        <DocumentsSection docs={documents} onChange={setDocuments}/>
      </div>

      {/* Duplicate warnings */}
      {duplicateNameWarning && (
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-[10px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[13px] text-amber-700 dark:text-amber-300">
          <span className="text-amber-500 shrink-0 mt-0.5">⚠</span>
          <span>{duplicateNameWarning} Double-check this is a different person before saving.</span>
        </div>
      )}
      {duplicatePhoneWarning && (
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-[10px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[13px] text-amber-700 dark:text-amber-300">
          <span className="text-amber-500 shrink-0 mt-0.5">⚠</span>
          <span>{duplicatePhoneWarning} Verify you have the correct phone number.</span>
        </div>
      )}

      {serverError && (
        <div className="px-4 py-3 rounded-[10px] bg-[var(--danger)]/10 border border-[var(--danger)]/30 text-[13px] text-[var(--danger)]">
          {serverError}
        </div>
      )}

      <div className="flex gap-3">
        <Button type="submit" loading={submitting} size="lg" icon={Save} className="flex-1 sm:flex-none sm:px-8">
          Save Employee
        </Button>
      </div>
    </form>

    {showFieldsModal && <EmployeeFieldsModal onClose={() => setShowFieldsModal(false)}/>}
    {showCustomBuilder && <CustomFieldBuilderModal onClose={() => setShowCustomBuilder(false)}/>}
</>
  );
}
